// Path: libs/core/theme/src/lib/data-access/brand-kernel.service.ts
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  computed,
  effect,
  inject,
  Injectable,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { APP_CONFIG, AppConfig } from '@cardapio-online/config';
import { firstValueFrom } from 'rxjs';

type BrandPatch = Partial<
  Pick<
    AppConfig,
    'appName' | 'brandPrimary' | 'brandLogoUrl' | 'mascoteLogo' | 'heroBannerUrl'
  >
>;

export type CompanyContactInfo = {
  phone?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  instagram?: string | null;
  website?: string | null;
};

export type CompanyAddressInfo = {
  city?: string | null;
  state?: string | null;
  line1?: string | null;
  country?: string | null;
};

type CompanyBrandApiResponse = {
  company: {
    id?: string;
    name?: string | null;
    primary?: string | null;
    logo?: {
      url?: string | null;
    } | null;
    extra?: {
      mascoteLogo?: string | null;
      heroBannerUrl?: string | null;
    } | null;
    address?: CompanyAddressInfo | null;
    contact?: CompanyContactInfo | null;
    updatedAt?: string | null;
  } | null;
};

@Injectable({
  providedIn: 'root',
})
export class BrandKernelService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly fallbackConfig = inject(APP_CONFIG);

  private readonly hasBackendBrand = signal(false);

  private readonly state = signal<AppConfig>({
    ...this.fallbackConfig,
  });

  private readonly _companyContact = signal<CompanyContactInfo | null>(null);
  private readonly _companyAddress = signal<CompanyAddressInfo | null>(null);

  readonly config = computed(() => this.state());

  readonly appName = computed(() => this.state().appName);
  readonly brandPrimary = computed(() => this.state().brandPrimary);
  readonly brandLogoUrl = computed(() => this.state().brandLogoUrl);
  readonly mascoteLogo = computed(() => this.state().mascoteLogo);
  readonly heroBannerUrl = computed(() => this.state().heroBannerUrl);

  readonly companyContact = computed(() => this._companyContact());
  readonly companyAddress = computed(() => this._companyAddress());

private get apiUrl(): string {
  return this.fallbackConfig.brandKernelApiUrl ?? '/company';
}

  constructor() {
    effect(() => {
      const config = this.state();

      if (!isPlatformBrowser(this.platformId)) {
        return;
      }

      this.applyCssVariables(config);
      this.document.title = config.appName || this.fallbackConfig.appName;
    });
  }

  async bootstrap(): Promise<void> {
    await this.refresh();
  }

  async refresh(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<CompanyBrandApiResponse>(this.apiUrl)
      );

      const patch = this.mapCompanyResponseToBrandPatch(response);

      if (!patch) {
        this.applyFallbackOnlyIfNoBackendBrand();
        return;
      }

      this.hasBackendBrand.set(true);
      this._companyContact.set(response.company?.contact ?? null);
      this._companyAddress.set(response.company?.address ?? null);
      this.apply(patch, 'api');
    } catch {
      this.applyFallbackOnlyIfNoBackendBrand();
    }
  }

  patch(update: BrandPatch): void {
    this.apply(update, 'runtime');
  }

  /**
   * Compatibilidade com telas antigas da POC.
   * Não persiste no backend.
   * Persistência real deve acontecer via CompanyService -> /company.
   */
  async update(update: BrandPatch): Promise<void> {
    this.patch(update);
  }

  private applyFallbackOnlyIfNoBackendBrand(): void {
    if (this.hasBackendBrand()) {
      return;
    }

    this.apply(this.fallbackConfig, 'fallback');
  }

  private mapCompanyResponseToBrandPatch(
    response: CompanyBrandApiResponse
  ): BrandPatch | null {
    const company = response.company;

    if (!company) {
      return null;
    }

    const patch = this.compactBrandPatch({
      appName: company.name ?? undefined,
      brandPrimary: company.primary ?? undefined,
      brandLogoUrl: company.logo?.url ?? undefined,
      mascoteLogo: company.extra?.mascoteLogo ?? undefined,
      heroBannerUrl: company.extra?.heroBannerUrl ?? undefined,
    });

    return Object.keys(patch).length ? patch : null;
  }

  private apply(update: BrandPatch, source: 'api' | 'runtime' | 'fallback'): void {
    const next: AppConfig = {
      ...this.fallbackConfig,
      ...this.state(),
      ...this.compactBrandPatch(update),
    };

    this.state.set(next);

    if (isPlatformBrowser(this.platformId)) {
      this.document.documentElement.dataset['brandSource'] = source;
    }
  }

  private compactBrandPatch(update: BrandPatch): BrandPatch {
    return Object.entries(update).reduce((result, [key, value]) => {
      const normalized = String(value ?? '').trim();

      if (normalized) {
        result[key as keyof BrandPatch] = normalized as never;
      }

      return result;
    }, {} as BrandPatch);
  }

  private applyCssVariables(config: AppConfig): void {
    const root = this.document.documentElement;

    const primary = config.brandPrimary || this.fallbackConfig.brandPrimary;
    const brandLogoUrl = config.brandLogoUrl || this.fallbackConfig.brandLogoUrl;
    const mascoteLogo = config.mascoteLogo || this.fallbackConfig.mascoteLogo;
    const heroBannerUrl = config.heroBannerUrl || this.fallbackConfig.heroBannerUrl;

    root.style.setProperty('--brand-primary', primary);
    root.style.setProperty('--shell-brand', primary);
    root.style.setProperty('--accent', primary);
    root.style.setProperty('--ds-color-primary', primary);

    root.style.setProperty('--brand-logo-url', `url("${brandLogoUrl}")`);
    root.style.setProperty('--mascote-logo-url', `url("${mascoteLogo}")`);

    if (heroBannerUrl) {
      root.style.setProperty('--hero-banner-url', `url("${heroBannerUrl}")`);
    }
  }
}